import {useState, useContext, useEffect, useRef, useMemo} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { H2, Panel, Flex, FlexItem, Button, Text, Box, Modal, Form, FormGroup, Input, InlineMessage, ProgressCircle, Fieldset ,Textarea, Datepicker, Select } from '@bigcommerce/big-design';
import { ArrowBackIcon, EditIcon, CheckIcon, DeleteIcon } from '@bigcommerce/big-design-icons';
import Container from '../../ui/Container';
import AppContext from '../../AppContext';
import { toEventData, toProductData, toNoonUTC } from '../../../utils/format-event';
import FieldTable from './FieldTable';

export default function EditEvent({ }) {
    const { user_token, store_hash } = useContext(AppContext);

    const [event, setEventData] = useState({});
    const [locationData, setLocationData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [initialData, setInitialData] = useState(null);
    const [modifiers, setModifiers] = useState([]);
    const [hasChanged, setHasChanged] = useState(false);
    const [isError, setIsError] = useState(false);

    console.log("event", event, modifiers);

    let params = useParams();
    const navigate = useNavigate();
    const handleBack = () => {
        navigate('/events');
    };

    const autoCompleteRef = useRef();
    const inputRef = useRef();
    const options = {
        componentRestrictions: { country: "us" },
        fields: ["address_components", "geometry", "icon", "name", "place_id", "formatted_address", "utc_offset_minutes", "url", "website", "formatted_phone_number"],
    };
    const editorRef = useRef(null);
    
    useEffect(() => {
        getProduct();
    }, []);

    useEffect(() => {
        if (!isLoading) {
            autoCompleteRef.current = new window.google.maps.places.Autocomplete(
                inputRef.current,
                options
            );
            autoCompleteRef.current.addListener("place_changed", async function () {
                const place = await autoCompleteRef.current.getPlace();
                console.log(place);
                console.log(place.geometry.location.lat());
                console.log(place.geometry.location.lng());
                setLocationData(place)
               });
        }
    }, [isLoading]);

    const getProduct = (id) => {
        setIsError(false);
        fetch(`${process.env.REACT_APP_API_URL}/events/getEventProduct?signed_payload=${user_token}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    store_hash: store_hash,
                    eventId: params.id ? params.id : 'new'
                })
            })
            .then(response => response.json())
            .then(data => {
                console.log("data", data);
                setInitialData(data);
                if (data.modifiers && data.modifiers.length > 0) {
                    let tempModifiers = data.modifiers.sort((a, b) => a.sort_order - b.sort_order)
                    if (params.id === 'new' && tempModifiers.length > 0) {
                        tempModifiers = tempModifiers.map((m, i) => {
                            m.id = `field_${i + 1}`;
                            m.updated = true;
                            if (m.option_values && m.option_values.length > 0) {
                                if (m.type !== 'checkbox') {
                                    m.option_values = m.option_values.map((v, j) => {
                                        v.id = `option_${Math.random().toString(36).substring(7)}`;
                                        delete v.option_id;
                                        return v;
                                    });
                                }
                                else delete m.option_values;
                            }
                            delete m.product_id;
                            delete m.name
                            return m;
                        });
                    }
                    setModifiers(tempModifiers);
                }
                setEventData(toEventData(data.product, params.id ? params.id : 'new'));
                setIsLoading(false);
            })
            .catch((error) => {
                console.error('Error:', error);
            });
    }

    const handleChange = (name, value) => {
        setEventData(prevState => ({
            ...prevState,
            [name]: value
        }));
        setHasChanged(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);
        const formData = new FormData(e.target);
        formData.append('eventData_startDate', event.eventData_startDate);
        formData.append('eventData_endDate', event.eventData_endDate);
        formData.append('eventData_endSaleDate', event.eventData_endSaleDate);
        if (locationData.geometry) {
            formData.append('eventData_coordinates', `${locationData.geometry.location.lat()},${locationData.geometry.location.lng()}`);
        }
        if (editorRef.current) {
            formData.append('description', editorRef.current.getContent());
        }

        formData.append('eventData_formattedAddress', locationData.formatted_address || "");
        formData.append('eventData_phone', locationData.formatted_phone_number || "");
        formData.append('eventData_website', locationData.website || "");
        formData.append('eventData_url', locationData.url || "");

        const productData = toProductData(params.id, formData, initialData.product);
        console.log("eventData", productData);
        let body = {
            store_hash: store_hash,
            productData: productData
        }
        if (params.id && params.id !== 'new') {
            body.productId = params.id;
        }

        const tempModifiers = [...modifiers];

        const locationModIndex = tempModifiers.findIndex(m => m.display_name === 'Location');

        if (locationModIndex > -1) {
            tempModifiers[locationModIndex].config.default_value = productData.preorder_message
            tempModifiers[locationModIndex].updated = true;
        }

        // If event is new, only include modifiers that are updated and not deleted
        if (params.id === 'new') {
            body.modifiers = tempModifiers.filter(m => m.updated && !m.deleted);
        } else {
            body.modifiers = tempModifiers.filter(m => m.updated);
        }

        console.log("body", body);
        fetch(`${process.env.REACT_APP_API_URL}/events/setEventProduct?signed_payload=${user_token}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        })
        .then(response => response.json())
        .then(data => {
            console.log("data", data);
            if (data.message) {
                setEventData(toEventData(productData));
                setIsError(data.message.title || 'Error saving event');
                setIsLoading(false);
                return
            }

            navigate(`/events`)
        })
        .catch((error) => {
            console.error('Error:', JSON.stringify(error));
        });
    };

    return (
        <Container className="checkout">
            <Box>
                <Flex justifyContent="space-between">
                    <FlexItem>
                        <H2>{params.id === 'new' && "New"} Event</H2>
                    </FlexItem>
                    <FlexItem>
                        <Button onClick={handleBack} variant='secondary' iconLeft={<ArrowBackIcon />}>
                            Back
                        </Button>
                    </FlexItem>
                </Flex>
                <Panel marginTop={'large'}>
                    {!isLoading ? <Form fullWidth={true} onSubmit={handleSubmit}>
                            <FormGroup>
                                <Input label="Title" name='name' defaultValue={event.name || ""} required />
                            </FormGroup>
                            <FormGroup>
                                <Editor
                                    tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
                                    onInit={(evt, editor) => editorRef.current = editor}
                                    initialValue={event.description}
                                    init={{
                                        height: 400,
                                        menubar: false,
                                        plugins: [
                                            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                                            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                            'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
                                        ],
                                        toolbar: 'undo redo | blocks | ' +
                                            'bold italic forecolor | alignleft aligncenter ' +
                                            'alignright alignjustify | bullist numlist outdent indent | ' +
                                            'removeformat | help',
                                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                                    }}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Textarea maxLength={250} label="Short Description" name='eventData_shortDescription' defaultValue={event.eventData_shortDescription || ""} />
                            </FormGroup>
                            <FormGroup>
                                <Input label="Location" name='eventData_location' ref={inputRef} defaultValue={event.eventData_location || ""} />
                            </FormGroup>
                            <FormGroup>
                                <Input label="Venue Name" name='eventData_venueName' defaultValue={event.eventData_venueName || locationData.name || ""} />
                            </FormGroup>
                            <FormGroup>
                                <Input label="Cost" name='price' required defaultValue={event.price || ""} />
                                <Input label="Tickets Available" type='number' name='inventory_level' required defaultValue={event.inventory_level || ""} />
                            </FormGroup>
                            <FormGroup>
                                <Input label="SKU" name='sku' required defaultValue={event.sku || ""} />
                                <Select label="Timezone" name='eventData_timezone' required value={event.eventData_timezone || ""}
                                options={[
                                    {value: 'EST', content: 'EST'},
                                    {value: 'EDT', content: 'EDT'},
                                    {value: 'CST', content: 'CST'},
                                    {value: 'CDT', content: 'CDT'},
                                    {value: 'MST', content: 'MST'},
                                    {value: 'MDT', content: 'MDT'},
                                    {value: 'PST', content: 'PST'},
                                    {value: 'PDT', content: 'PDT'},
                                    {value: 'AKST', content: 'AKST'},
                                    {value: 'AKDT', content: 'AKDT'},
                                    {value: 'HST', content: 'HST'},
                                    {value: 'HDT', content: 'HDT'}
                                  ]} />
                            </FormGroup>
                            <FormGroup>
                                <Datepicker
                                    dateFormat="MMMM d, yyyy"
                                    label="Start Date"
                                    onDateChange={(date) => handleChange("eventData_startDate", date && toNoonUTC(date))}
                                    value={event.eventData_startDate || toNoonUTC()}
                                    required
                                    name='eventData_startDate'
                                />
                                <Input type='time' label="Start Time" onChange={(time) => handleChange("eventData_startTime", time.target.value)} name='eventData_startTime' value={event.eventData_startTime || "08:00"} />
                            </FormGroup>
                            <FormGroup>
                                <Datepicker
                                    dateFormat="MMMM d, yyyy"
                                    label="End Date"
                                    onDateChange={(date) => handleChange("eventData_endDate", date && toNoonUTC(date))}
                                    value={event.eventData_endDate || toNoonUTC()}
                                    required
                                    name='eventData_endDate'
                                />
                                <Input type='time' label="End Time" onChange={(time) => handleChange("eventData_endTime", time.target.value)} name='eventData_endTime' value={event.eventData_endTime || "17:00"} />
                            </FormGroup>
                            <FormGroup>
                                <Datepicker
                                    dateFormat="MMMM d, yyyy"
                                    label="End Sale Date"
                                    onDateChange={(date) => handleChange("eventData_endSaleDate", date && toNoonUTC(date))}
                                    value={event.eventData_endSaleDate || toNoonUTC()}
                                    required
                                    name='eventData_endSaleDate'
                                />
                                <Input type='time' label="End Sale Time" onChange={(time) => handleChange("eventData_endSaleTime", time.target.value)} name='eventData_endSaleTime' value={event.eventData_endSaleTime || ""} />
                            </FormGroup>
                            <Fieldset legend="Attendee Information" description={'Information to collect from attendee.'}>
                                {!isLoading && <FieldTable modifiers={modifiers} setModifiers={setModifiers} setHasChanged={setHasChanged} />}
                            </Fieldset>
                            <Box marginTop="xxLarge">
                                {isError !== false && <InlineMessage marginBottom={'large'} type="error" messages={[{ text: isError }]} />}
                                <Button type="submit">{params.id === 'new' ? "Create" : "Save"}</Button>
                            </Box>
                        </Form> : <div className={"loading-circle"}>
                            <ProgressCircle size="large" />
                        </div>}
                </Panel>
            </Box>
        </Container>
    );
}
